import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MCPanelHeader } from '@/components/mc/MCSection';
import { ArrowLeft, RefreshCw, ChevronLeft, ChevronRight, Loader2, CheckCircle2, XCircle, AlertCircle, Clock, ImageOff, Wifi, WifiOff, ExternalLink, ShieldCheck, ShieldX, Undo2, FileSearch, ListChecks, Server, } from 'lucide-react';
import { useAuth } from '../../../hooks/use-auth';
import { useWsEvent } from '@/hooks/use-websocket';
import { WsStatusDot } from '@/components/WsStatusDot';
import { ManagerPinPad } from '@/components/ManagerPinPad';
import { verifyManager } from '@/services/pos-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { VENUE_TIMEZONE, todayDateString, toDateString, buildAtlanticDate } from '@/lib/timezone';
import { OCR_MANAGER_RETURN_GRACE_KEY, buildOcrBookingDetailUrl, getOcrManagerReturnGraceExpiry, isOcrBookingReturnPath, isOcrManagerReturnGraceActive, } from '@/lib/ocr-return';
const getApiBase = () => process.env.REACT_APP_API_BASE || 'http://localhost:8080';
const REVIEW_FILTERS = ['all', 'needs_review', 'verified', 'rejected'];
function parseReviewFilter(value) {
    return REVIEW_FILTERS.includes(value) ? value : 'needs_review';
}
function readReceiptAnalysisRouteState() {
    if (typeof window === 'undefined') {
        return { date: todayDateString(), filter: 'needs_review', paymentId: null };
    }
    const params = new URLSearchParams(window.location.search);
    return {
        date: params.get('date') || todayDateString(),
        filter: parseReviewFilter(params.get('filter')),
        paymentId: params.get('paymentId'),
    };
}
function isOcrBookingReturnRoute() {
    if (typeof window === 'undefined')
        return false;
    return isOcrBookingReturnPath(window.location.pathname, window.location.search);
}
function markOcrManagerReturnGrace() {
    if (typeof window === 'undefined')
        return;
    try {
        window.sessionStorage.setItem(OCR_MANAGER_RETURN_GRACE_KEY, String(getOcrManagerReturnGraceExpiry()));
    }
    catch {
        // sessionStorage can be unavailable in private/restricted browser contexts.
    }
}
function hasOcrManagerReturnGrace() {
    if (typeof window === 'undefined')
        return false;
    try {
        const rawValue = window.sessionStorage.getItem(OCR_MANAGER_RETURN_GRACE_KEY);
        if (isOcrManagerReturnGraceActive(rawValue))
            return true;
        window.sessionStorage.removeItem(OCR_MANAGER_RETURN_GRACE_KEY);
    }
    catch {
        return false;
    }
    return false;
}
function clearOcrManagerReturnGrace() {
    if (typeof window === 'undefined')
        return;
    try {
        window.sessionStorage.removeItem(OCR_MANAGER_RETURN_GRACE_KEY);
    }
    catch {
        // sessionStorage can be unavailable in private/restricted browser contexts.
    }
}
function formatTime(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Halifax',
    });
}
function formatDateTime(dateStr) {
    return new Date(dateStr).toLocaleString('en-US', {
        timeZone: VENUE_TIMEZONE,
    });
}
function formatMoney(amount) {
    return amount == null ? '—' : `$${Number(amount).toFixed(2)}`;
}
function amountMatches(systemAmount, parsedAmount) {
    return parsedAmount != null && Math.abs(systemAmount - parsedAmount) <= 0.02;
}
function getEffectiveStatus(item) {
    if (!item.receiptPath)
        return 'NO_RECEIPT';
    return item.effectiveStatus || item.analysis?.matchStatus || 'PENDING';
}
function statusTone(status) {
    switch (status) {
        case 'MATCHED':
        case 'MANUALLY_VERIFIED':
            return 'success';
        case 'MISMATCH':
        case 'UNREADABLE':
        case 'MANUALLY_REJECTED':
            return 'danger';
        case 'PENDING':
        case 'ANALYZING':
        case 'NO_RECEIPT':
            return 'warning';
        default:
            return 'neutral';
    }
}
function statusClass(tone) {
    switch (tone) {
        case 'success':
            return 'mc-status-success';
        case 'danger':
            return 'mc-status-danger';
        case 'warning':
            return 'mc-status-warning';
        case 'info':
            return 'mc-status-info';
        default:
            return 'border-[color:var(--mc-divider)] text-[color:var(--mc-text-meta)]';
    }
}
function StatusBadge({ status }) {
    switch (status) {
        case 'MATCHED':
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-success gap-1", children: [_jsx(CheckCircle2, { className: "w-3 h-3" }), " Matched"] }));
        case 'MANUALLY_VERIFIED':
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-success gap-1", children: [_jsx(ShieldCheck, { className: "w-3 h-3" }), " Verified (review)"] }));
        case 'MANUALLY_REJECTED':
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-danger gap-1", children: [_jsx(ShieldX, { className: "w-3 h-3" }), " Rejected (review)"] }));
        case 'MISMATCH':
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-danger gap-1", children: [_jsx(XCircle, { className: "w-3 h-3" }), " Mismatch"] }));
        case 'UNREADABLE':
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-danger gap-1", children: [_jsx(AlertCircle, { className: "w-3 h-3" }), " Unreadable"] }));
        case 'PENDING':
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-warning gap-1", children: [_jsx(Clock, { className: "w-3 h-3" }), " Pending"] }));
        case 'ANALYZING':
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-info gap-1 animate-pulse", children: [_jsx(Loader2, { className: "w-3 h-3 animate-spin" }), " Analyzing..."] }));
        default:
            return (_jsxs(Badge, { className: "mc-status-badge mc-status-warning gap-1", children: [_jsx(ImageOff, { className: "w-3 h-3" }), " No Receipt"] }));
    }
}
function manualReviewActorLabel(analysis) {
    if (analysis.manualReviewManager?.name) {
        const staffName = analysis.manualReviewer?.name;
        return [
            `by ${analysis.manualReviewManager.name} (Manager PIN)`,
            staffName ? `staff ${staffName}` : undefined,
        ].filter(Boolean).join(' · ');
    }
    return `by ${analysis.manualReviewer?.name || 'unknown'}`;
}
function OcrMetricTile({ label, value, detail, tone = 'neutral', }) {
    return (_jsxs("div", { className: `mc-metric-tile ${statusClass(tone)}`, children: [_jsx("span", { children: label }), _jsx("strong", { className: "text-2xl leading-tight", children: value }), detail && _jsx("div", { className: "mc-meta-dim mt-1", children: detail })] }));
}
function ParsedAmountTile({ label, value, systemAmount, }) {
    const isMatch = amountMatches(systemAmount, value);
    return (_jsxs("div", { className: `mc-metric-tile ${isMatch ? 'mc-status-success' : 'border-[color:var(--mc-divider)]'}`, children: [_jsx("span", { children: label }), _jsx("strong", { children: formatMoney(value) }), isMatch && _jsx("div", { className: "mc-meta-dim mt-1", children: "matched payment" })] }));
}
function OcrHealthChip({ piHealth, onRefresh }) {
    if (piHealth === null) {
        return (_jsxs("div", { className: "mc-chip mc-mono", children: [_jsx(Loader2, { className: "h-3.5 w-3.5 animate-spin" }), "OCR checking"] }));
    }
    const reachable = piHealth.reachable;
    return (_jsxs("button", { type: "button", className: `mc-chip mc-mono ${reachable ? '' : 'mc-chip-alert'}`, onClick: onRefresh, "aria-label": "Refresh OCR health", children: [reachable ? _jsx(Wifi, { className: "h-3.5 w-3.5" }) : _jsx(WifiOff, { className: "h-3.5 w-3.5" }), _jsx("span", { children: reachable ? 'Pi OCR Online' : 'Pi OCR Offline' }), _jsx("span", { className: "mc-meta-dim", children: reachable
                    ? `${piHealth.modelLoaded ? 'model loaded' : 'model loading'}${piHealth.responseTimeMs != null ? ` · ${piHealth.responseTimeMs}ms` : ''}`
                    : piHealth.error || 'unreachable' })] }));
}
export default function ReceiptAnalysisPage() {
    const { user, isLoading } = useAuth();
    const navigate = useNavigate();
    const initialRouteState = useMemo(() => readReceiptAnalysisRouteState(), []);
    const isAdmin = user?.role === 'ADMIN';
    const isStaff = user?.role === 'STAFF';
    const backTarget = isAdmin ? '/pos/dashboard' : '/dashboard';
    const backLabel = isAdmin ? 'Admin' : 'Dashboard';
    const [date, setDate] = useState(() => initialRouteState.date);
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [piHealth, setPiHealth] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState(initialRouteState.paymentId);
    const [reanalyzing, setReanalyzing] = useState(null);
    const [filter, setFilter] = useState(initialRouteState.filter);
    // Manual-review modal state
    const [reviewModal, setReviewModal] = useState(null);
    const [reviewNote, setReviewNote] = useState('');
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [reviewError, setReviewError] = useState(null);
    const [clearingReview, setClearingReview] = useState(null);
    const [accessState, setAccessState] = useState('checking');
    const [managerName, setManagerName] = useState('');
    const [managerPinError, setManagerPinError] = useState(null);
    const [managerPinLoading, setManagerPinLoading] = useState(false);
    const canUseReceiptAnalysis = isAdmin || accessState === 'granted';
    const autoLockReadyRef = useRef(false);
    const shouldAutoLockRef = useRef(false);
    const retainManagerAccessForReturnRef = useRef(false);
    const checkManagerAccess = useCallback(async () => {
        setManagerPinError(null);
        if (!user) {
            setAccessState('forbidden');
            return;
        }
        if (user.role === 'ADMIN') {
            setAccessState('granted');
            return;
        }
        if (user.role !== 'STAFF') {
            setAccessState('forbidden');
            return;
        }
        setAccessState('checking');
        try {
            const res = await fetch(`${getApiBase()}/api/receipt-analysis/manager-access`, {
                credentials: 'include',
            });
            if (res.ok) {
                const data = await res.json();
                setManagerName(data.employeeName || 'Manager');
                setAccessState('granted');
                clearOcrManagerReturnGrace();
                return;
            }
            setAccessState('pin_required');
            if (res.status !== 403)
                setManagerPinError('Unable to check manager access.');
        }
        catch {
            setAccessState('pin_required');
            setManagerPinError('Unable to check manager access.');
        }
    }, [user]);
    useEffect(() => {
        if (isLoading)
            return;
        checkManagerAccess();
    }, [checkManagerAccess, isLoading]);
    useEffect(() => {
        shouldAutoLockRef.current = user?.role === 'STAFF';
    }, [user?.role]);
    useEffect(() => {
        const readyTimer = window.setTimeout(() => {
            autoLockReadyRef.current = true;
        }, 0);
        const lockManagerAccess = () => {
            if (!autoLockReadyRef.current || !shouldAutoLockRef.current)
                return;
            if (retainManagerAccessForReturnRef.current ||
                isOcrBookingReturnRoute() ||
                hasOcrManagerReturnGrace()) {
                return;
            }
            void fetch(`${getApiBase()}/api/receipt-analysis/manager-access`, {
                method: 'DELETE',
                credentials: 'include',
                keepalive: true,
            }).catch((err) => {
                console.error('Failed to lock OCR Analysis manager access', err);
            });
        };
        window.addEventListener('pagehide', lockManagerAccess);
        return () => {
            window.clearTimeout(readyTimer);
            window.removeEventListener('pagehide', lockManagerAccess);
            lockManagerAccess();
        };
    }, []);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const params = new URLSearchParams(window.location.search);
        params.set('date', date);
        params.set('filter', filter);
        if (selectedPaymentId) {
            params.set('paymentId', selectedPaymentId);
        }
        else {
            params.delete('paymentId');
        }
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        if (nextUrl !== currentUrl) {
            window.history.replaceState(window.history.state, '', nextUrl);
        }
    }, [date, filter, selectedPaymentId]);
    const fetchPiHealth = useCallback(async () => {
        if (!canUseReceiptAnalysis)
            return;
        try {
            const res = await fetch(`${getApiBase()}/api/receipt-analysis/health`, {
                credentials: 'include',
            });
            if (res.ok) {
                setPiHealth(await res.json());
            }
        }
        catch {
            setPiHealth({ reachable: false, error: 'Failed to reach backend' });
        }
    }, [canUseReceiptAnalysis]);
    const fetchData = useCallback(async () => {
        if (!canUseReceiptAnalysis)
            return;
        setLoading(true);
        try {
            const [listRes, summaryRes] = await Promise.all([
                fetch(`${getApiBase()}/api/receipt-analysis?date=${date}`, { credentials: 'include' }),
                fetch(`${getApiBase()}/api/receipt-analysis/summary?startDate=${date}&endDate=${date}`, {
                    credentials: 'include',
                }),
            ]);
            if (listRes.status === 403 || summaryRes.status === 403) {
                setAccessState(isStaff ? 'pin_required' : 'forbidden');
                return;
            }
            if (listRes.ok) {
                const data = await listRes.json();
                setItems(data);
            }
            if (summaryRes.ok) {
                const data = await summaryRes.json();
                setSummary(data);
            }
        }
        catch (err) {
            console.error('Failed to fetch receipt analysis data', err);
        }
        finally {
            setLoading(false);
        }
    }, [canUseReceiptAnalysis, date, isStaff]);
    useEffect(() => {
        if (canUseReceiptAnalysis) {
            fetchData();
            fetchPiHealth();
        }
    }, [canUseReceiptAnalysis, fetchData, fetchPiHealth]);
    // Realtime: refetch the list when receipts change; update Pi health on transition
    useWsEvent('receipt.analysis_complete', () => { if (canUseReceiptAnalysis)
        fetchData(); });
    useWsEvent('receipt.uploaded', () => { if (canUseReceiptAnalysis)
        fetchData(); });
    useWsEvent('receipt.deleted', () => { if (canUseReceiptAnalysis)
        fetchData(); });
    useWsEvent('receipt.manual_review', () => { if (canUseReceiptAnalysis)
        fetchData(); });
    useWsEvent('ocr.pi_health_changed', (evt) => {
        if (canUseReceiptAnalysis)
            setPiHealth(evt.payload);
    });
    const submitManagerPin = async (pin) => {
        if (!pin) {
            setManagerPinError('Enter a manager PIN.');
            return;
        }
        setManagerPinLoading(true);
        setManagerPinError(null);
        try {
            const result = await verifyManager(pin);
            if (result.authorized) {
                setManagerName(result.employeeName || 'Manager');
                setAccessState('granted');
            }
            else {
                setManagerPinError(result.reason || 'Manager PIN required.');
            }
        }
        catch (err) {
            setManagerPinError(err instanceof Error ? err.message : 'Manager PIN verification failed.');
        }
        finally {
            setManagerPinLoading(false);
        }
    };
    const handleReanalyze = async (paymentId) => {
        setReanalyzing(paymentId);
        try {
            const res = await fetch(`${getApiBase()}/api/receipt-analysis/${paymentId}/reanalyze`, {
                method: 'POST',
                credentials: 'include',
            });
            if (res.ok) {
                // WS `receipt.analysis_complete` will refresh the list automatically
                // once the background job finishes. Refresh once now to flip status
                // to ANALYZING immediately.
                fetchData();
            }
        }
        catch (err) {
            console.error('Re-analysis failed', err);
        }
        finally {
            setReanalyzing(null);
        }
    };
    const openReviewModal = (mode, item) => {
        setReviewModal({
            mode,
            paymentId: item.paymentId,
            customerName: item.booking.customerName,
            defaultNote: item.analysis?.manualReviewNote || '',
        });
        setReviewNote(item.analysis?.manualReviewNote || '');
        setReviewError(null);
    };
    const closeReviewModal = () => {
        if (reviewSubmitting)
            return;
        setReviewModal(null);
        setReviewNote('');
        setReviewError(null);
    };
    const submitReview = async () => {
        if (!reviewModal)
            return;
        const { mode, paymentId } = reviewModal;
        const trimmed = reviewNote.trim();
        if (mode === 'reject' && !trimmed) {
            setReviewError('A reason note is required when rejecting.');
            return;
        }
        if (trimmed.length > 500) {
            setReviewError('Note must be 500 characters or fewer.');
            return;
        }
        setReviewSubmitting(true);
        setReviewError(null);
        try {
            const res = await fetch(`${getApiBase()}/api/receipt-analysis/${paymentId}/${mode}`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ note: trimmed || undefined }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Request failed (${res.status})`);
            }
            // WS will broadcast; fetch immediately for snappy UX
            fetchData();
            setReviewModal(null);
            setReviewNote('');
        }
        catch (err) {
            setReviewError(err instanceof Error ? err.message : 'Submission failed');
        }
        finally {
            setReviewSubmitting(false);
        }
    };
    const handleClearReview = async (paymentId) => {
        if (!window.confirm('Clear this manual review decision? The auto verdict will apply again.')) {
            return;
        }
        setClearingReview(paymentId);
        try {
            const res = await fetch(`${getApiBase()}/api/receipt-analysis/${paymentId}/review`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (res.ok)
                fetchData();
        }
        catch (err) {
            console.error('Clear review failed', err);
        }
        finally {
            setClearingReview(null);
        }
    };
    const changeDate = (delta) => {
        // Shift by `delta` Atlantic days. Using toISOString().slice(0, 10)
        // would have returned the UTC date, drifting by 1 day for the last
        // few hours of every Atlantic day.
        const [y, m, d] = date.split('-').map(Number);
        const base = buildAtlanticDate(y, m, d, 12, 0, 0);
        const shifted = new Date(base.getTime() + delta * 24 * 60 * 60 * 1000);
        setDate(toDateString(shifted));
    };
    const filteredItems = items.filter((it) => {
        if (filter === 'all')
            return true;
        if (filter === 'needs_review') {
            return (it.analysis &&
                (it.analysis.matchStatus === 'MISMATCH' || it.analysis.matchStatus === 'UNREADABLE') &&
                it.analysis.manualReviewStatus === null);
        }
        if (filter === 'verified')
            return it.analysis?.manualReviewStatus === 'VERIFIED';
        if (filter === 'rejected')
            return it.analysis?.manualReviewStatus === 'REJECTED';
        return true;
    });
    const selectedItem = filteredItems.find((it) => it.paymentId === selectedPaymentId) || filteredItems[0] || null;
    const buildBookingDetailUrl = useCallback((item) => {
        const status = getEffectiveStatus(item);
        return buildOcrBookingDetailUrl({
            bookingId: item.booking.id,
            paymentId: item.paymentId,
            seatIndex: item.seatIndex,
            status,
            date,
            filter,
            reason: item.analysis?.mismatchReason,
        });
    }, [date, filter]);
    const openBookingDetail = useCallback((item) => {
        retainManagerAccessForReturnRef.current = true;
        markOcrManagerReturnGrace();
        navigate(buildBookingDetailUrl(item));
    }, [buildBookingDetailUrl, navigate]);
    const needsReviewTotal = summary ? (summary.needsReview ?? summary.mismatch + summary.unreadable) : 0;
    const queueTotal = summary ? (summary.pending ?? 0) + (summary.analyzing ?? 0) + summary.noReceipt : 0;
    const resolvedTotal = summary
        ? summary.matched + (summary.manuallyVerified ?? 0) + (summary.manuallyRejected ?? 0)
        : 0;
    const reviewAuditLabel = !isAdmin && managerName
        ? `Authorized by ${managerName} · Staff ${user?.name || user?.email || 'staff'}`
        : `Reviewer ${user?.name || 'Admin'}`;
    if (isLoading || (!isAdmin && accessState === 'checking')) {
        return (_jsx("div", { className: "kg-page mc-root", children: _jsx("div", { className: "mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4", children: _jsxs("div", { className: "mc-panel text-center", children: [_jsx(Loader2, { className: "mx-auto mb-3 h-6 w-6 animate-spin text-[color:var(--mc-cyan)]" }), _jsx("p", { className: "mc-section-label", children: "Checking OCR Analysis access..." }), _jsx("p", { className: "mc-meta mt-1", children: "Verifying account role and Manager PIN scope." })] }) }) }));
    }
    if (accessState === 'forbidden') {
        return (_jsx("div", { className: "kg-page mc-root", children: _jsx("div", { className: "max-w-xl mx-auto px-4 py-16", children: _jsxs("div", { className: "mc-panel", children: [_jsx(MCPanelHeader, { label: "OCR Analysis access required", meta: "Admin login or staff Manager PIN unlock is required.", border: true }), _jsxs("div", { className: "pt-4", children: [_jsx("p", { className: "mc-meta", children: "Sign in as an admin, or use a staff account and unlock this page with a Manager PIN." }), _jsxs("button", { type: "button", className: "mc-action-btn mc-action-btn-fit mt-4", onClick: () => navigate(backTarget), children: [_jsx(ArrowLeft, { className: "h-4 w-4" }), "Back to ", backLabel] })] })] }) }) }));
    }
    if (accessState === 'pin_required') {
        return (_jsx("div", { className: "kg-page mc-root", children: _jsx(Dialog, { open: true, onOpenChange: (open) => {
                    if (!open && !managerPinLoading)
                        navigate(backTarget);
                }, children: _jsx(DialogContent, { className: "mc-dialog-content p-0 sm:max-w-sm", children: _jsxs("div", { className: "mc-dialog-frame", children: [_jsx("div", { className: "mc-dialog-frame-accent bg-[color:var(--mc-cyan)]" }), _jsx(DialogHeader, { className: "mc-dialog-header", children: _jsxs("div", { children: [_jsx(DialogTitle, { children: "Manager PIN required" }), _jsx(DialogDescription, { className: "mc-meta", children: "Manager authorization is required before opening OCR Analysis." })] }) }), _jsxs("div", { className: "mc-dialog-body", children: [_jsx(ManagerPinPad, { onSubmit: submitManagerPin, error: managerPinError, loading: managerPinLoading, prompt: "Enter a Manager PIN to unlock OCR Analysis for this staff session.", submitLabel: "Unlock OCR Analysis", showRemember: false }), _jsx("p", { className: "mc-meta-dim text-center", children: "Access auto-locks when this page closes." })] }), _jsx(DialogFooter, { className: "mc-dialog-footer sm:justify-center", children: _jsx(Button, { type: "button", variant: "ghost", onClick: () => navigate(backTarget), children: "Cancel" }) })] }) }) }) }));
    }
    return (_jsxs("div", { className: "kg-page mc-root", children: [_jsxs("div", { className: "mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 lg:px-6", children: [_jsxs("header", { className: "mc-panel", children: [_jsxs("div", { className: "flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between", children: [_jsxs("div", { className: "min-w-0", children: [_jsxs("div", { className: "mc-kicker mb-2 flex items-center gap-2", children: [_jsx(FileSearch, { className: "h-4 w-4 text-[color:var(--mc-cyan)]" }), "OCR Control Center"] }), _jsx("h1", { className: "text-2xl font-semibold tracking-tight text-[color:var(--mc-text-hero)]", children: "Receipt Analysis" }), _jsx("p", { className: "mc-meta mt-1 max-w-2xl", children: "Review card and gift-card receipt OCR, resolve mismatches, and keep the operational queue clean." })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs("button", { type: "button", className: "mc-chip", onClick: () => navigate(backTarget), children: [_jsx(ArrowLeft, { className: "h-3.5 w-3.5" }), backLabel] }), !isAdmin && managerName && (_jsxs("span", { className: "mc-chip mc-mono text-[color:var(--mc-cyan)]", children: [_jsx(ShieldCheck, { className: "h-3.5 w-3.5" }), "Manager access: ", managerName] })), _jsx(OcrHealthChip, { piHealth: piHealth, onRefresh: fetchPiHealth }), _jsx(WsStatusDot, {})] })] }), _jsxs("div", { className: "mt-5 flex flex-col gap-3 border-t border-[color:var(--mc-divider-soft)] pt-4 lg:flex-row lg:items-center lg:justify-between", children: [_jsxs("div", { className: "mc-inline-cluster", children: [_jsx("button", { type: "button", className: "mc-action-btn mc-action-btn-fit mc-action-btn-compact", onClick: () => changeDate(-1), children: _jsx(ChevronLeft, { className: "h-4 w-4" }) }), _jsx("input", { type: "date", value: date, onChange: (e) => setDate(e.target.value), className: "mc-input mc-mono min-h-[2.35rem]" }), _jsx("button", { type: "button", className: "mc-action-btn mc-action-btn-fit mc-action-btn-compact", onClick: () => changeDate(1), children: _jsx(ChevronRight, { className: "h-4 w-4" }) }), _jsxs("button", { type: "button", className: "mc-action-btn mc-action-btn-fit mc-action-btn-accent mc-action-btn-compact", onClick: fetchData, disabled: loading, children: [_jsx(RefreshCw, { className: `h-4 w-4 ${loading ? 'animate-spin' : ''}` }), "Refresh"] })] }), _jsxs("div", { className: "mc-meta-dim mc-mono flex items-center gap-2", children: [_jsx(Server, { className: "h-3.5 w-3.5" }), "Scope: CARD / GIFT_CARD payments only"] })] })] }), summary && (_jsxs("section", { className: "grid gap-3 md:grid-cols-2 xl:grid-cols-5", "aria-label": "OCR analysis summary", children: [_jsx(OcrMetricTile, { label: "Needs Review", value: needsReviewTotal, detail: `${summary.mismatch} mismatch · ${summary.unreadable} unreadable`, tone: needsReviewTotal > 0 ? 'danger' : 'success' }), _jsx(OcrMetricTile, { label: "Queue", value: queueTotal, detail: `${summary.pending} pending · ${summary.analyzing ?? 0} analyzing · ${summary.noReceipt} no receipt`, tone: queueTotal > 0 ? 'warning' : 'neutral' }), _jsx(OcrMetricTile, { label: "Resolved", value: resolvedTotal, detail: `${summary.matched} matched · ${summary.manuallyVerified ?? 0} verified · ${summary.manuallyRejected ?? 0} rejected`, tone: "success" }), _jsx(OcrMetricTile, { label: "Matched Amount", value: summary.matchedAmount, detail: `of ${summary.totalAmount} total`, tone: "info" }), _jsx(OcrMetricTile, { label: "Visible Queue", value: filteredItems.length, detail: `filter: ${filter.replace('_', ' ')}`, tone: "neutral" })] })), _jsxs("section", { className: "grid min-h-[560px] gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.65fr)]", children: [_jsxs("div", { className: "mc-panel flex min-h-0 flex-col", children: [_jsx(MCPanelHeader, { label: "Receipt Queue", meta: "Select a payment to inspect OCR details and review actions.", border: true, right: _jsx("div", { className: "mc-inline-cluster", children: [
                                                { key: 'needs_review', label: 'Needs review', count: summary?.needsReview },
                                                { key: 'all', label: 'All', count: summary?.total },
                                                { key: 'verified', label: 'Verified', count: summary?.manuallyVerified },
                                                { key: 'rejected', label: 'Rejected', count: summary?.manuallyRejected },
                                            ].map((f) => (_jsxs("button", { type: "button", onClick: () => setFilter(f.key), className: `mc-chip mc-mono ${filter === f.key ? 'border-[color:var(--mc-cyan)] text-[color:var(--mc-cyan)]' : ''}`, children: [f.label, typeof f.count === 'number' && _jsxs("span", { className: "mc-meta-dim", children: ["(", f.count, ")"] })] }, f.key))) }) }), _jsx("div", { className: "mt-4 min-h-0 flex-1 overflow-y-auto pr-1 mc-scroll-thin", children: loading ? (_jsxs("div", { className: "mc-empty-state", children: [_jsx(Loader2, { className: "mx-auto mb-3 h-8 w-8 animate-spin text-[color:var(--mc-cyan)]" }), _jsx("div", { className: "mc-section-label", children: "Loading receipt queue" })] })) : filteredItems.length === 0 ? (_jsxs("div", { className: "mc-empty-state", children: [_jsx(ListChecks, { className: "mx-auto mb-3 h-8 w-8 text-[color:var(--mc-green)]" }), _jsx("div", { className: "mc-section-label", children: filter === 'needs_review'
                                                        ? '// no receipts need review'
                                                        : '// no receipts in this filter' }), _jsx("p", { className: "mc-meta mt-1", children: "Try another filter or date." })] })) : (_jsx("div", { className: "flex flex-col gap-2", children: filteredItems.map((item) => {
                                                const status = getEffectiveStatus(item);
                                                const tone = statusTone(status);
                                                const isSelected = selectedItem?.paymentId === item.paymentId;
                                                return (_jsx("button", { type: "button", onClick: () => setSelectedPaymentId(item.paymentId), className: [
                                                        'mc-row w-full text-left',
                                                        isSelected ? 'border-[color:var(--mc-cyan)] bg-[rgba(29,224,197,0.08)]' : '',
                                                    ].filter(Boolean).join(' '), children: _jsxs("div", { className: "flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between", children: [_jsxs("div", { className: "min-w-0", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("span", { className: "mc-mono text-xs text-[color:var(--mc-text-meta)]", children: formatTime(item.createdAt) }), _jsx("span", { className: `mc-dot ${tone === 'success' ? 'mc-dot-cyan' : tone === 'danger' ? 'mc-dot-magenta' : tone === 'warning' ? 'mc-dot-purple' : 'mc-dot-gray'}` }), _jsx("span", { className: "font-semibold text-[color:var(--mc-text-primary)]", children: item.booking.customerName || 'Quick Sale' })] }), _jsxs("div", { className: "mc-meta mt-1 truncate", children: [item.booking.roomName, " \u00B7 Seat ", item.seatIndex, " \u00B7 ", item.paymentId.slice(0, 8)] })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3 text-right sm:grid-cols-4 lg:min-w-[430px]", children: [_jsxs("div", { children: [_jsx("div", { className: "mc-kicker", children: "System" }), _jsx("div", { className: "mc-mono text-[color:var(--mc-text-primary)]", children: formatMoney(item.amount) })] }), _jsxs("div", { children: [_jsx("div", { className: "mc-kicker", children: "OCR" }), _jsx("div", { className: "mc-mono text-[color:var(--mc-text-primary)]", children: formatMoney(item.analysis?.extractedAmount) })] }), _jsx("div", { className: "sm:col-span-2", children: _jsx(StatusBadge, { status: status }) })] })] }) }, item.paymentId));
                                            }) })) })] }), _jsxs("aside", { className: "mc-panel min-h-0", children: [_jsx(MCPanelHeader, { label: "Receipt Dossier", meta: selectedItem ? 'Focused payment review packet' : 'Select a receipt queue row', border: true }), !selectedItem ? (_jsxs("div", { className: "mc-empty-state mt-4", children: [_jsx(FileSearch, { className: "mx-auto mb-3 h-8 w-8 text-[color:var(--mc-text-meta)]" }), _jsx("div", { className: "mc-section-label", children: "// dossier idle" }), _jsx("p", { className: "mc-meta mt-1", children: "Choose a queue row to inspect OCR and review options." })] })) : (_jsxs("div", { className: "mt-4 flex flex-col gap-4", children: [_jsxs("div", { className: "mc-subpanel", children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("div", { className: "mc-kicker", children: "Customer" }), _jsx("div", { className: "text-lg font-semibold text-[color:var(--mc-text-hero)]", children: selectedItem.booking.customerName || 'Quick Sale' }), _jsxs("div", { className: "mc-meta", children: [selectedItem.booking.roomName, " \u00B7 Seat ", selectedItem.seatIndex] })] }), _jsx(StatusBadge, { status: getEffectiveStatus(selectedItem) })] }), _jsxs("button", { type: "button", className: "mc-action-btn mc-action-btn-fit mc-action-btn-compact mt-4", onClick: () => openBookingDetail(selectedItem), children: [_jsx(ExternalLink, { className: "h-4 w-4" }), "View booking"] })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsxs("div", { className: "mc-metric-tile", children: [_jsx("span", { children: "System amount" }), _jsx("strong", { children: formatMoney(selectedItem.amount) })] }), _jsxs("div", { className: `mc-metric-tile ${amountMatches(selectedItem.amount, selectedItem.analysis?.extractedAmount) ? 'mc-status-success' : 'mc-status-danger'}`, children: [_jsx("span", { children: "Matched OCR amount" }), _jsx("strong", { children: formatMoney(selectedItem.analysis?.extractedAmount) })] })] }), _jsxs("div", { className: "mc-subpanel", children: [_jsx(MCPanelHeader, { label: "Parsed amount lines", meta: "Raw terminal amount breakdown used for matching", tight: true, flush: true }), _jsxs("div", { className: "mt-3 grid gap-3 sm:grid-cols-3", children: [_jsx(ParsedAmountTile, { label: "Amount line", value: selectedItem.analysis?.amounts?.amount, systemAmount: selectedItem.amount }), _jsx(ParsedAmountTile, { label: "Tip line", value: selectedItem.analysis?.amounts?.tip, systemAmount: selectedItem.amount }), _jsx(ParsedAmountTile, { label: "Total line", value: selectedItem.analysis?.amounts?.total, systemAmount: selectedItem.amount })] })] }), _jsxs("div", { className: "mc-subpanel", children: [_jsx(MCPanelHeader, { label: "OCR fields", tight: true, flush: true }), _jsxs("dl", { className: "mt-3 grid grid-cols-2 gap-3 text-sm", children: [_jsxs("div", { children: [_jsx("dt", { className: "mc-kicker", children: "Card" }), _jsx("dd", { className: "mc-mono", children: selectedItem.analysis?.cardLast4 ? `${selectedItem.analysis.cardType || selectedItem.method} ····${selectedItem.analysis.cardLast4}` : selectedItem.method })] }), _jsxs("div", { children: [_jsx("dt", { className: "mc-kicker", children: "Approval" }), _jsx("dd", { className: "mc-mono", children: selectedItem.analysis?.approvalCode || '—' })] }), _jsxs("div", { children: [_jsx("dt", { className: "mc-kicker", children: "Terminal" }), _jsx("dd", { className: "mc-mono", children: selectedItem.analysis?.terminalId || '—' })] }), _jsxs("div", { children: [_jsx("dt", { className: "mc-kicker", children: "Model" }), _jsx("dd", { children: selectedItem.analysis?.modelUsed || '—' })] }), _jsxs("div", { children: [_jsx("dt", { className: "mc-kicker", children: "Receipt date" }), _jsx("dd", { children: selectedItem.analysis?.transactionDate || '—' })] }), _jsxs("div", { children: [_jsx("dt", { className: "mc-kicker", children: "Receipt time" }), _jsx("dd", { children: selectedItem.analysis?.transactionTime || '—' })] })] })] }), selectedItem.analysis?.mismatchReason && (_jsxs("div", { className: "mc-subpanel mc-subpanel-danger", children: [_jsx("div", { className: "mc-kicker text-[color:var(--mc-magenta)]", children: "Mismatch reason" }), _jsx("p", { className: "mt-1 text-sm text-[color:var(--mc-text-primary)]", children: selectedItem.analysis.mismatchReason })] })), selectedItem.analysis?.manualReviewStatus && (_jsxs("div", { className: "mc-subpanel mc-subpanel-success", children: [_jsxs("div", { className: "flex items-center gap-2", children: [selectedItem.analysis.manualReviewStatus === 'VERIFIED'
                                                                ? _jsx(ShieldCheck, { className: "h-4 w-4 text-[color:var(--mc-green)]" })
                                                                : _jsx(ShieldX, { className: "h-4 w-4 text-[color:var(--mc-magenta)]" }), _jsx("span", { className: "font-semibold", children: selectedItem.analysis.manualReviewStatus === 'VERIFIED' ? 'Manually verified' : 'Manually rejected' })] }), _jsxs("p", { className: "mc-meta mt-1", children: [manualReviewActorLabel(selectedItem.analysis), selectedItem.analysis.manualReviewAt && ` · ${formatDateTime(selectedItem.analysis.manualReviewAt)}`] }), selectedItem.analysis.manualReviewNote && (_jsxs("p", { className: "mt-3 whitespace-pre-wrap text-sm", children: ["\u201C", selectedItem.analysis.manualReviewNote, "\u201D"] }))] })), _jsxs("div", { className: "mc-subpanel", children: [_jsx("div", { className: "mc-kicker", children: "Analyzed" }), _jsx("p", { className: "mc-meta mt-1", children: selectedItem.analysis?.analyzedAt ? formatDateTime(selectedItem.analysis.analyzedAt) : 'No analysis record yet' })] }), _jsxs("div", { className: "grid gap-2 sm:grid-cols-3", children: [selectedItem.receiptPath && (_jsxs("button", { type: "button", className: "mc-action-btn mc-action-btn-info", disabled: reanalyzing === selectedItem.paymentId || selectedItem.analysis?.matchStatus === 'ANALYZING', onClick: () => handleReanalyze(selectedItem.paymentId), children: [reanalyzing === selectedItem.paymentId ? _jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : _jsx(RefreshCw, { className: "h-4 w-4" }), "Re-analyze"] })), selectedItem.receiptPath && selectedItem.analysis?.manualReviewStatus === null && (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "mc-action-btn mc-action-btn-success", onClick: () => openReviewModal('verify', selectedItem), children: [_jsx(ShieldCheck, { className: "h-4 w-4" }), "Verify"] }), _jsxs("button", { type: "button", className: "mc-action-btn mc-action-btn-danger", onClick: () => openReviewModal('reject', selectedItem), children: [_jsx(ShieldX, { className: "h-4 w-4" }), "Reject"] })] })), selectedItem.receiptPath && selectedItem.analysis?.manualReviewStatus !== null && selectedItem.analysis && (_jsxs("button", { type: "button", className: "mc-action-btn mc-action-btn-danger sm:col-span-2", disabled: clearingReview === selectedItem.paymentId, onClick: () => handleClearReview(selectedItem.paymentId), children: [clearingReview === selectedItem.paymentId ? _jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : _jsx(Undo2, { className: "h-4 w-4" }), "Clear review"] }))] })] }))] })] })] }), _jsx(Dialog, { open: !!reviewModal, onOpenChange: (open) => { if (!open)
                    closeReviewModal(); }, children: _jsx(DialogContent, { className: "mc-dialog-content p-0 sm:max-w-md", children: _jsxs("div", { className: "mc-dialog-frame", children: [_jsx("div", { className: `mc-dialog-frame-accent ${reviewModal?.mode === 'verify' ? 'bg-[color:var(--mc-green)]' : 'bg-[color:var(--mc-magenta)]'}` }), _jsx(DialogHeader, { className: "mc-dialog-header", children: _jsxs("div", { children: [_jsx(DialogTitle, { children: reviewModal?.mode === 'verify' ? 'Verify receipt' : 'Reject receipt' }), _jsx(DialogDescription, { className: "mc-meta", children: reviewModal?.mode === 'verify'
                                                ? `Mark this receipt as manually verified for ${reviewModal?.customerName}. Optional note.`
                                                : `Reject this receipt for ${reviewModal?.customerName}. A reason note is required.` })] }) }), _jsxs("div", { className: "mc-dialog-body space-y-3", children: [_jsxs("div", { className: "mc-subpanel mc-subpanel-accent", children: [_jsx("div", { className: "mc-kicker", children: "Review audit" }), _jsx("p", { className: "mc-meta mt-1", children: reviewAuditLabel })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("textarea", { autoFocus: true, value: reviewNote, onChange: (e) => setReviewNote(e.target.value), maxLength: 500, rows: 4, placeholder: reviewModal?.mode === 'verify'
                                                    ? 'Optional: anything noteworthy about this receipt'
                                                    : 'Required: why are you rejecting this receipt?', className: "mc-input w-full resize-none text-sm placeholder:text-[color:var(--mc-text-meta-dim)]" }), _jsx("div", { className: "flex items-center justify-between text-xs", children: _jsx("span", { className: reviewError ? 'text-[color:var(--mc-magenta)]' : 'mc-meta-dim', children: reviewError || `${reviewNote.length}/500` }) })] })] }), _jsxs(DialogFooter, { className: "mc-dialog-footer mc-dialog-footer-end", children: [_jsx(Button, { variant: "ghost", onClick: closeReviewModal, disabled: reviewSubmitting, children: "Cancel" }), _jsxs(Button, { onClick: submitReview, disabled: reviewSubmitting, className: reviewModal?.mode === 'verify'
                                            ? 'mc-action-btn-success mc-action-btn-fit'
                                            : 'mc-action-btn-danger mc-action-btn-fit', children: [reviewSubmitting ? (_jsx(Loader2, { className: "w-4 h-4 animate-spin mr-1" })) : reviewModal?.mode === 'verify' ? (_jsx(ShieldCheck, { className: "w-4 h-4 mr-1" })) : (_jsx(ShieldX, { className: "w-4 h-4 mr-1" })), reviewModal?.mode === 'verify' ? 'Verify' : 'Reject'] })] })] }) }) })] }));
}
